import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import {
  ApiConnectionActivityLog,
  Connection,
  ConnectionTypes,
  getRealChannelId,
  ImportedConnectionActivityLog,
} from '@tv3/store/connection/connection.model'
import { forkJoin, Observable, of } from 'rxjs'
import { Channel, ChannelNameTokens, ChannelTypes, deserializeArray, isSomething } from '@tokeet/tv3-platform'
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators'
import * as R from 'ramda'
import {
  AddExportedCalendarConnectionPayload,
  AddImportedCalendarConnectionPayload,
  ApiConnectionStatus,
  ChannelRoomRateResponse,
  ChannelRoomsResponse,
  channelRoomToConnectionPlainObject,
  CreateChannelListingPayload,
  GetChannelRoomRatesPayload,
  LinkConnectionWithRentalPayload,
  PushRatesToApiConnectionPayload,
  RefreshImportedCalendarConnectionPayload,
  UnlinkConnectionWithRentalPayload,
} from '@tv3/store/connection/connection.types'
import * as lodash from 'lodash'
import { environment } from '../../../environments/environment'
import {
  HoliduChannelService,
  HVMIChannelService,
  RentalsUnitedChannelService,
  TiketChannelService,
  GVRChannelService,
  CtripChannelService,
} from '@tokeet-frontend/channels'

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  constructor(
    private http: HttpClient,
    private gvrChannelService: GVRChannelService,
    private holidu: HoliduChannelService,
    private hvmi: HVMIChannelService,
    private rentalsUnited: RentalsUnitedChannelService,
    private tiketService: TiketChannelService,
    private ctripService: CtripChannelService
  ) {}

  static getConnectionChannelType(connectionType: ConnectionTypes) {
    switch (connectionType) {
      case ConnectionTypes.ExportedICal:
      case ConnectionTypes.ImportedICal:
        return ChannelTypes.ICal
      case ConnectionTypes.Api:
        return ChannelTypes.Auto
      default:
        return ''
    }
  }

  static getConnectionChannel(conn: Connection, channels: Channel[]) {
    let channel = R.find((c: Channel) => c.id === getRealChannelId(conn.channelId))(channels)

    if (!channel) {
      // no channel id in connections of ical type
      channel = R.find((ch) => {
        const channelName = R.toLower(ch.name + '')
        return (
          (channelName === R.toLower(conn.source + '') ||
            channelName === R.toLower(conn.name + '') ||
            ch?.friendlyName?.toLowerCase().indexOf((conn.source + '').toLowerCase()) !== -1) &&
          ch.type.indexOf(ConnectionService.getConnectionChannelType(conn.type)) > -1
        )
      }, channels) // imported calendars/API connections
    }

    return channel
  }

  static getBackendURLIdentityForChannel(channelName: ChannelNameTokens, includeNew = false) {
    let url = ''
    switch (channelName) {
      case ChannelNameTokens.ExpediaAPI:
        url = 'expedia'
        break
      case ChannelNameTokens.BookingAPI:
        url = 'bdc'
        break
      case ChannelNameTokens.AirBnBV2API:
        url = 'abbv2'
        break
      case ChannelNameTokens.AirBnBAPI:
        url = 'abb'
        break
      case ChannelNameTokens.AgodaAPI:
        url = 'agoda'
        break
      case ChannelNameTokens.HomeToGo:
        url = 'hometogo'
        break
      case ChannelNameTokens.Homeaway:
        url = 'homeaway'
        break
    }

    if (includeNew) {
      switch (channelName) {
        case ChannelNameTokens.Holidu:
          url = 'holidu'
          break
        case ChannelNameTokens.Hvmi:
          url = 'hvmi'
          break
        case ChannelNameTokens.RentalsUnited:
          url = 'rentalsunited'
          break
        case ChannelNameTokens.Tiket:
          url = 'tiket'
          break
        case ChannelNameTokens.Trip:
          url = 'ctrip'
          break
        case ChannelNameTokens.GoogleVacationRentals:
          url = 'gvr'
          break
      }
    }

    return url
  }

  all(): Observable<Connection[]> {
    return forkJoin([
      this.http.get<object[]>('@api/channel/all/').pipe(
        map((channels) =>
          R.reject(
            (c: any) =>
              R.isNil(c.channel_id) &&
              R.isNil(c.propertyName) &&
              R.isNil(c.propertyId) &&
              R.isNil(c.rental_id) &&
              R.isNil(c.linkDate) &&
              R.isNil(c.name),
            channels
          )
        ),
        map((response) =>
          R.map((r: any) => {
            r.legacyId = r.id
            delete r.id
            return r
          }, response)
        )
      ),
      environment.features?.hvmi ? this.hvmi.getConnections().pipe(catchError(() => of([]))) : of([]),
      environment.features?.holidu ? this.holidu.getConnections().pipe(catchError(() => of([]))) : of([]),
      environment.features?.tiket ? this.tiketService.getConnections().pipe(catchError(() => of([]))) : of([]),
      environment.features?.ctrip ? this.ctripService.getConnections().pipe(catchError(() => of([]))) : of([]),
      environment.features?.rentalsunited ? this.rentalsUnited.getConnections().pipe(catchError(() => of([]))) : of([]),
      environment.features?.gvr ? this.gvrChannelService.getConnections().pipe(catchError(() => of([]))) : of([]),
    ]).pipe(
      map((data) => R.flatten(data)),
      deserializeArray<Connection>(Connection)
    )
  }

  get(channelName: ChannelNameTokens, roomId: any, propertyId: any) {
    let observer = of(null)
    if (channelName === ChannelNameTokens.RentalsUnited) {
      observer = this.rentalsUnited.getConnection(propertyId)
    }

    return observer.pipe(
      filter(isSomething),
      map((data) => Connection.deserialize(data))
    )
  }

  allByChannel(channelName: ChannelNameTokens, channelId: string): Observable<Connection[]> {
    if (channelName === ChannelNameTokens.Holidu) {
      return this.holidu.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.Hvmi) {
      return this.hvmi.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.RentalsUnited) {
      return this.rentalsUnited.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.GoogleVacationRentals) {
      return this.gvrChannelService.getConnections().pipe(deserializeArray<Connection>(Connection))
    }

    let url = ''
    const channelPath = ConnectionService.getBackendURLIdentityForChannel(channelName)
    url = `@api/${channelPath}/rooms/${channelId}`
    return this.http.get(url).pipe(
      map((res: ChannelRoomsResponse[]) =>
        R.map((room: ChannelRoomsResponse) => {
          return channelRoomToConnectionPlainObject(room)
        }, res)
      ),
      deserializeArray<Connection>(Connection)
    )
  }

  allByRental(rentalId: string): Observable<Connection[]> {
    const url = `@api/channel/rental/api/${rentalId}`

    return this.http.get(url).pipe(deserializeArray<Connection>(Connection))
  }

  refreshForChannel(channelName: ChannelNameTokens, channelId: string) {
    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.getConnections()
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getConnections()
    }

    const channelPath = ConnectionService.getBackendURLIdentityForChannel(channelName)
    if (!channelPath) {
      return of([])
    }

    const url = `@api/${channelPath}/refresh/rooms/${channelId}`
    return this.http.get(url)
  }

  getExportedCalendarsByChannelName(channelName: string): Observable<Connection[]> {
    const url = `@api/channel/exported/all/`
    return this.http.get<object[]>(url).pipe(
      map((res) =>
        R.filter((item: any) => {
          const name = (item.name + '').toLowerCase()
          return name.indexOf((channelName + '').toLowerCase()) > -1
        }, res)
      ),
      deserializeArray<Connection>(Connection)
    )
  }

  addImportedCalendarConnection(payload: AddImportedCalendarConnectionPayload): Observable<Connection> {
    const url = `@api/calendar/import`

    return this.http.put(url, payload).pipe(map((res) => Connection.deserialize(res)))
  }

  addExportedCalendarConnection(payload: AddExportedCalendarConnectionPayload): Observable<Connection> {
    const url = `@api/channel/`

    return this.http.post(url, payload).pipe(map((res) => Connection.deserialize(res)))
  }

  createListing(channelName: ChannelNameTokens, payload: CreateChannelListingPayload) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/publish/create/`
    return this.http.post(url, payload)
  }

  link(payload: LinkConnectionWithRentalPayload) {
    const url = '@api/channel/rental/link'
    return this.http.put(url, payload)
  }

  linkABBV1(payload: LinkConnectionWithRentalPayload) {
    const url = '@api/abb/rental/link'
    return this.http.put(url, payload)
  }

  linkABBV2(payload: LinkConnectionWithRentalPayload) {
    const url = '@api/abbv2/rental/link'
    return this.http.put(url, payload)
  }

  unlink(payload: UnlinkConnectionWithRentalPayload) {
    const url = '@api/channel/rental/unlink'
    return this.http.put(url, payload)
  }

  unlinkABB(payload: UnlinkConnectionWithRentalPayload) {
    const url = '@api/abb/rental/unlink'
    return this.http.put(url, payload)
  }

  unlinkABBV2(payload: UnlinkConnectionWithRentalPayload) {
    const url = '@api/abbv2/rental/unlink'
    return this.http.put(url, payload)
  }

  importBookingByInquiryIdABBV2(channelId: string, connection: Connection, inquiryId: string) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(
      ChannelNameTokens.AirBnBV2API
    )}/import/old/bookings/${channelId}`
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post(url, { ...connection.serialize(), conf_code: inquiryId })
  }

  importBookings(channelName: ChannelNameTokens, channelId: string, connection: Connection) {
    let url = ''
    switch (channelName) {
      case ChannelNameTokens.ExpediaAPI:
        url = `@api/expedia/import/bookings/${channelId}`
        break
      default:
        url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/import/old/bookings/${channelId}`
        break
    }
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post(url, connection.serialize())
  }

  pushAvailability(channelName: ChannelNameTokens, channelId: string, connection: Connection) {
    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.pusAvailability(connection.roomId as number, {
        hotel_id: connection.propertyId as number,
        rental_id: connection.rentalId,
      })
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.pusAvailability(connection.roomId as number, {
        hotel_id: connection.propertyId as number,
        rental_id: connection.rentalId,
      })
    }

    if (channelName === ChannelNameTokens.GoogleVacationRentals) {
      return this.gvrChannelService.pushAvailability(connection.roomId as string)
    }

    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/push/availability/${channelId}`
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post(url, connection.serialize())
  }

  pushRates(channelName: ChannelNameTokens, channelId: string, payload: PushRatesToApiConnectionPayload) {
    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.pushRates(payload.roomId as number, {
        rental_id: payload.rental_id,
        hotel_id: payload.propertyId as number,
        rate_plan_code: lodash.toNumber(payload.rateid),
        category: payload.category,
      })
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.pushRates(payload.roomId as number, {
        rental_id: payload.rental_id,
        hotel_id: payload.propertyId as number,
        rate_plan_code: lodash.toNumber(payload.rateid),
        category: payload.category,
      })
    }

    if (channelName === ChannelNameTokens.GoogleVacationRentals) {
      return this.gvrChannelService.pushRates(payload.roomId as string)
    }

    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/push/rates/${channelId}`
    return this.http.post(url, payload)
  }

  getRoomRatesFromConnection(
    channelName: ChannelNameTokens,
    channelId: string,
    payload: GetChannelRoomRatesPayload
  ): Observable<ChannelRoomRateResponse[]> {
    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.getRoomRatePlans(payload.propertyId).pipe(
        map((items) =>
          items.map(
            (item) =>
              ({
                id: lodash.toString(item.code),
                name: item.name,
              } as ChannelRoomRateResponse)
          )
        )
      )
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getRoomRatePlans(payload.propertyId).pipe(
        map((items) =>
          items.map(
            (item) =>
              ({
                id: lodash.toString(item.code),
                name: item.name,
              } as ChannelRoomRateResponse)
          )
        )
      )
    } else {
      const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/roomrates/${channelId}`
      return this.http.post<ChannelRoomRateResponse[]>(url, payload)
    }
  }

  getApiConnectionLog(
    channelName: ChannelNameTokens,
    propertyId: string | number,
    roomId?: string | number
  ): Observable<ApiConnectionActivityLog[]> {
    const path = ConnectionService.getBackendURLIdentityForChannel(channelName, true)
    if (!path) {
      return of([])
    }
    const url = `@api/${path}/activity/log/${propertyId}?${!!roomId ? `roomid=${roomId}` : ''}`
    return this.http.get(url).pipe(deserializeArray<ApiConnectionActivityLog>(ApiConnectionActivityLog))
  }

  getApiConnectionStatus(
    channelName: ChannelNameTokens,
    propertyId: string | number,
    roomId: string | number
  ): Observable<ApiConnectionStatus> {
    const path = ConnectionService.getBackendURLIdentityForChannel(channelName)
    if (!path) {
      return of({})
    }
    const url = `@api/${path}/update/log/${propertyId}?roomid=${roomId}`
    return this.http.get<ApiConnectionStatus>(url)
  }

  // This method deletes a connected iCal rental from the Tokeet API, not the Channel Manager.
  deleteExportedCalendar(id: string) {
    const url = `@api/channel/delete/${id}`
    return this.http.delete(url)
  }

  getImportedConnectionLog(connectionId: string): Observable<ImportedConnectionActivityLog[]> {
    const url = `@api/calendar/import/logs/${connectionId}`
    return this.http.get(url).pipe(deserializeArray<ImportedConnectionActivityLog>(ImportedConnectionActivityLog))
  }

  refreshImportedCalendar(payload: RefreshImportedCalendarConnectionPayload): Observable<Connection> {
    const url = `@api/calendar/import`
    return this.http.put(url, payload).pipe(map((res) => Connection.deserialize(res)))
  }

  deleteImportedCalendar(calendarId: string, rentalId: string, removeAllEvents: boolean) {
    const url = `@api/calendar/import/delete/${rentalId}/${calendarId}?all=${removeAllEvents ? 1 : ''}`
    return this.http.delete(url)
  }

  getConnectionsStatuses(propertyIds: (string | number)[], roomIds: (string | number)[]) {
    const url = '@api/channel/connection/status/'
    return this.http.post(url, {
      properties: propertyIds,
      rooms: roomIds,
    })
  }
}
